import { defaultSettings, type Capabilities, type Layout, type Me, type Settings, type SettingsPatch } from '@jerome2/shared';
import { api } from '../api.ts';
import { createStore } from '../lib/store.ts';

// Toestand van de app zelf: wie is ingelogd, instellingen, verbinding en versie, wat de server kan
// (versturen, spraak), en of het scherm smal is (telefoon-indeling) met daarin de detailstand.

/** Dezelfde grens als in de schil: tot 900 px is het de telefoon-indeling. */
export const NARROW_QUERY = '(max-width: 900px)';
/** Vanaf 1280 px is er ruimte voor twee panelen en de werkerskolom naast het werk; daaronder (tablet, half scherm) één paneel en de werkers in een vel. */
export const WIDE_QUERY = '(min-width: 1280px)';

export interface AppState {
  me: Me | null;
  settings: Settings;
  wsStatus: 'verbinden' | 'open' | 'dicht';
  serverVersion: string | null;
  /** De server draait inmiddels een nieuwere versie dan deze client. */
  newVersion: boolean;
  /** Wat de server kan (`GET /api/capabilities`); null zolang het nog niet geladen is. */
  capabilities: Capabilities | null;
  /** Smal scherm: één paneel, onderste navigatie, vellen in plaats van kolommen. */
  narrow: boolean;
  /** Breed scherm (vanaf 1280 px): twee panelen mogelijk en de werkerskolom naast het werk. */
  wide: boolean;
  /** Op de telefoon staat een mail of gesprek open: de onderste navigatie verdwijnt dan. */
  phoneDetail: boolean;
}

function matches(query: string, fallback: boolean): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : fallback;
}

export const app = createStore<AppState>({ me: null, settings: defaultSettings(), wsStatus: 'verbinden', serverVersion: null, newVersion: false, capabilities: null, narrow: matches(NARROW_QUERY, false), wide: matches(WIDE_QUERY, true), phoneDetail: false });

let capabilitiesLoading: Promise<Capabilities | null> | null = null;

/**
 * Laadt wat de server kan, één keer per sessie (gelijktijdige aanroepen delen het verzoek); bij een fout blijft het null
 * en probeert de volgende aanroep opnieuw. Achtergrondwerk: de server vraagt de connector naar Mail.Send, en dat gaat
 * niet voor wat Jeroen zelf doet.
 */
export function loadCapabilities(opts: { force?: boolean } = {}): Promise<Capabilities | null> {
  const known = app.get().capabilities;
  if (known && !opts.force) return Promise.resolve(known);
  if (capabilitiesLoading) return capabilitiesLoading;
  capabilitiesLoading = api<Capabilities>('/api/capabilities', { background: true })
    .then((caps) => {
      app.patch({ capabilities: caps });
      return caps;
    })
    .catch(() => null)
    .finally(() => {
      capabilitiesLoading = null;
    });
  return capabilitiesLoading;
}

/**
 * Na het inloggen: laadt de mogelijkheden, en na een onderbroken verbinding (meestal een nieuwe deploy, die een
 * ElevenLabs-sleutel kan hebben gekregen) opnieuw, maar alleen zolang spraak onbekend of uit is. Geeft de opruimer terug.
 */
export function watchCapabilities(): () => void {
  void loadCapabilities();
  let last = app.get().wsStatus;
  let everOpen = last === 'open';
  return app.subscribe(() => {
    const s = app.get();
    if (s.wsStatus === last) return;
    last = s.wsStatus;
    if (s.wsStatus !== 'open') return;
    if (everOpen && !s.capabilities?.voice?.dictation) void loadCapabilities({ force: true });
    everOpen = true;
  });
}

export function setMe(me: Me): void {
  app.patch({ me, settings: me.settings, serverVersion: me.version });
}

/** Eigen breedtes die nog onderweg zijn naar de server (saveLayout), en het volgnummer van de laatste. Zolang er een onderweg is,
 * houdt de client zijn eigen `layout` vast: een antwoord of websocketmelding van een eerdere wijziging zet een nieuwere breedte
 * anders even terug (twee randen kort na elkaar, of een paar pijltjes achter elkaar). */
let layoutPending = 0;
let layoutLatest = 0;

/** Instellingen van de server overnemen (een antwoord of de websocket); de eigen breedtes blijven staan zolang er een onderweg is. */
function adopt(settings: Settings, keepLayout = layoutPending > 0): void {
  app.patch({ settings: keepLayout ? { ...settings, layout: app.get().settings.layout } : settings });
}

export function applySettings(settings: Settings): void {
  adopt(settings);
}

export async function saveSettings(patch: SettingsPatch): Promise<Settings> {
  const saved = await api<Settings>('/api/settings', { method: 'PUT', body: patch });
  adopt(saved);
  return saved;
}

/**
 * Breedtes van de desktop bewaren (ticket 28376): meteen in de store, zodat het scherm ze toont en een volgende wijziging erop
 * voortbouwt, en dan met de hele `layout` naar de server (die neemt alleen de velden over die de client stuurt). Alleen het
 * antwoord op de laatste wijziging telt; mislukt die, dan komen de breedtes van ervoor terug en gaat de fout door.
 */
export async function saveLayout(patch: Partial<Layout>): Promise<Settings> {
  const current = app.get().settings;
  const before = current.layout;
  const layout = { ...before, ...patch };
  app.patch({ settings: { ...current, layout } });
  const n = ++layoutLatest;
  layoutPending += 1;
  try {
    const saved = await api<Settings>('/api/settings', { method: 'PUT', body: { layout } });
    layoutPending -= 1;
    adopt(saved, n !== layoutLatest);
    return saved;
  } catch (e) {
    layoutPending -= 1;
    if (n === layoutLatest) app.patch({ settings: { ...app.get().settings, layout: before } });
    throw e;
  }
}

export function noteServerVersion(version: string): void {
  const s = app.get();
  app.patch({ serverVersion: version, newVersion: Boolean(s.me && s.me.version !== version) });
}

export function setPhoneDetail(open: boolean): void {
  if (app.get().phoneDetail !== open) app.patch({ phoneDetail: open });
}
